import { createFileRoute, notFound, redirect } from "@tanstack/react-router";
import communityStyles from "../styles/community.css?url";
import { CommunityContent } from "../components/community/community-content";
import { DocumentContent } from "../components/document-content";
import { useShell } from "../lib/document-route";
import { creatorPageHead, shellFrom, withStylesheet } from "../lib/head";
import { loadCommunity } from "../server/community";
import { usePublishedData } from "../lib/published-data";

type Search = { notice?: string; error?: string; preview?: string };

const text = (value: unknown) => (typeof value === "string" && value ? value : undefined);

/**
 * Community (/community): the page the creator composed, with the feed at its built-in block, or
 * the native community. Only members with access receive posts; the page is never cached.
 */
export const Route = createFileRoute("/community/")({
  validateSearch: (search: Record<string, unknown>): Search => ({
    notice: text(search.notice),
    error: text(search.error),
    preview: search.preview === "1" ? "1" : undefined,
  }),
  loader: async () => {
    const data = await loadCommunity();
    if (data.kind === "missing") throw notFound();
    if (data.kind === "redirect") throw redirect({ href: data.to, statusCode: 302 });
    return data;
  },
  head: ({ matches, loaderData }) => {
    const shell = shellFrom(matches);
    if (!shell || !loaderData) return {};
    const head =
      loaderData.kind === "page"
        ? creatorPageHead(shell, {
            pathname: "/community",
            title: loaderData.page.seoTitle || loaderData.page.title,
            description: loaderData.page.description,
            noindex: loaderData.private || loaderData.locked || loaderData.page.hideFromSearch,
            preloadImage: loaderData.lead,
          })
        : creatorPageHead(shell, {
            pathname: "/community",
            title: `${loaderData.community.navTitle} | ${shell.creator.name}`,
            description:
              loaderData.community.intro ||
              `The private member community from ${shell.creator.name}.`,
            noindex: true,
          });
    return withStylesheet(head, communityStyles);
  },
  component: Community,
});

function Community() {
  const initial = Route.useLoaderData();
  const search = Route.useSearch();
  const shell = useShell();
  const { data, pending, failed } = usePublishedData(initial, shell, "/community", "community");
  const content = (
    <div data-site-operation-pending={pending ? "" : undefined} aria-busy={pending}>
      <CommunityContent
        community={data.community}
        notice={search.notice}
        error={failed ? "Member content could not be loaded. Please try again." : search.error}
      />
      <noscript>
        <a href="/community?server=1">Open the member view without JavaScript</a>
      </noscript>
    </div>
  );
  return data.kind === "page" ? (
    <DocumentContent data={data} shell={shell} builtIn={content} />
  ) : (
    content
  );
}
